import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRightOutlined, CheckOutlined, MailOutlined, MessageOutlined, PauseOutlined,
  PlayCircleOutlined, SendOutlined, ShopOutlined
} from '@ant-design/icons';
import type { AppLanguage } from '../../../i18n/languages';
import creatorPortraits from '../../../assets/images/home/creator-portraits.png';
import './proof.css';

const content = {
  zh: {
    eyebrow: 'YOUR NEXT COLLABORATION STARTS HERE', title: ['连接合适的人，', '让合作自然发生。'],
    description: '从一声问候，到一次合作。用更适合的方式触达达人，把每一次沟通变成新的可能。',
    users: '10万+', trust: '用户信赖与使用', stats: [['海量', '达人资源'], ['3 种', '建联方式'], ['一站式', '合作管理']],
    demo: '场景演示', workspace: '建联工作室', creators: '你的下一位合作伙伴',
    channels: ['邮件建联', '私信沟通', '定向邀约'],
    scenes: [
      { label: '用一封好邮件，开启新的可能', caption: '向达人介绍品牌，让合作邀约更有诚意。', title: '一起开启新品合作', body: 'Hi Alex，我们很喜欢你的内容，希望邀请你体验我们的新品，期待与你一起创造精彩。', status: '合作邮件', tag: '品牌故事，从这里开始' },
      { label: '让每一次对话，都离合作更近', caption: '及时回应兴趣，持续跟进有价值的沟通。', title: 'Alex', body: '你好！很期待了解这次合作。', status: '当然，我们一起聊聊合作细节。', tag: '把握每一个合作机会' },
      { label: '把合作意向，变成清晰的计划', caption: '关联商品与达人，开启定向合作。', title: '新品体验合作计划', body: '商品 · 达人 · 定向佣金', status: '定向合作邀约', tag: '好商品，值得被更多人看见' }
    ],
    pause: '暂停轮播', play: '播放轮播'
  },
  en: {
    eyebrow: 'YOUR NEXT COLLABORATION STARTS HERE', title: ['Meet the right creators.', 'Build what comes next.'],
    description: 'From a first hello to a new collaboration. Connect in the right way and make room for new possibilities.',
    users: '100K+', trust: 'users trust and use Wowbit', stats: [['Extensive', 'creator resources'], ['3', 'outreach channels'], ['All-in-one', 'collaboration management']],
    demo: 'Illustration', workspace: 'Outreach studio', creators: 'Your next collaboration starts with a connection',
    channels: ['Email outreach', 'Direct messages', 'Targeted invites'],
    scenes: [
      { label: 'A thoughtful email opens a new door', caption: 'Introduce your brand and give your invitation a personal touch.', title: 'Let’s create something together', body: 'Hi Alex, we love your content and would like to invite you to discover our latest product. We’d love to hear your ideas.', status: 'Collaboration email', tag: 'Your brand story starts here' },
      { label: 'Every conversation brings you closer', caption: 'Respond to interest and keep meaningful conversations going.', title: 'Alex', body: 'Hi! I’d love to learn more about this collaboration.', status: 'Of course. Let’s talk about the details.', tag: 'Make the most of every opportunity' },
      { label: 'Turn shared interest into a clear plan', caption: 'Bring products and creators together with a targeted invitation.', title: 'New product collaboration', body: 'Products · Creators · Commission', status: 'Targeted invitation', tag: 'Give great products a bigger audience' }
    ],
    pause: 'Pause slideshow', play: 'Play slideshow'
  },
  vi: {
    eyebrow: 'YOUR NEXT COLLABORATION STARTS HERE', title: ['Kết nối đúng người,', 'mở ra cơ hội hợp tác.'],
    description: 'Từ lời chào đầu tiên đến hợp tác mới. Chọn cách liên hệ phù hợp và mở ra những khả năng mới.',
    users: '100K+', trust: 'người dùng tin tưởng và sử dụng', stats: [['Đa dạng', 'nguồn nhà sáng tạo'], ['3', 'kênh liên hệ'], ['Tất cả trong một', 'quản lý hợp tác']],
    demo: 'Hình minh họa', workspace: 'Không gian kết nối', creators: 'Đối tác tiếp theo bắt đầu từ một kết nối',
    channels: ['Gửi email', 'Nhắn tin', 'Mời hợp tác'],
    scenes: [
      { label: 'Một email mở ra cơ hội mới', caption: 'Giới thiệu thương hiệu và gửi lời mời hợp tác chân thành.', title: 'Cùng hợp tác cho sản phẩm mới', body: 'Hi Alex, chúng tôi rất thích nội dung của bạn và muốn mời bạn trải nghiệm sản phẩm mới. Rất mong được hợp tác cùng bạn.', status: 'Email hợp tác', tag: 'Câu chuyện thương hiệu bắt đầu từ đây' },
      { label: 'Mỗi cuộc trò chuyện đưa hợp tác đến gần hơn', caption: 'Phản hồi sự quan tâm và duy trì những cuộc trao đổi có giá trị.', title: 'Alex', body: 'Xin chào! Tôi rất muốn tìm hiểu thêm về lần hợp tác này.', status: 'Tất nhiên, cùng trao đổi chi tiết nhé.', tag: 'Nắm bắt từng cơ hội hợp tác' },
      { label: 'Biến sự quan tâm thành kế hoạch rõ ràng', caption: 'Kết nối sản phẩm và nhà sáng tạo bằng lời mời hợp tác.', title: 'Hợp tác trải nghiệm sản phẩm mới', body: 'Sản phẩm · Nhà sáng tạo · Hoa hồng', status: 'Lời mời hợp tác', tag: 'Đưa sản phẩm tốt đến với nhiều người hơn' }
    ],
    pause: 'Tạm dừng trình chiếu', play: 'Phát trình chiếu'
  }
};

// Fictional decorative portraits share one 3 × 2 sheet.
function Portrait({ variant }: { variant: number }) {
  return <span className='studio-portrait' aria-hidden='true' style={{
    backgroundImage: `url(${creatorPortraits})`,
    backgroundPosition: `${(variant % 3) * 50}% ${Math.floor(variant / 3) * 100}%`
  }} />;
}

export function ProofSection({ language }: { language: AppLanguage }) {
  const c = content[language];
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setReducedMotion(preference.matches);
    updatePreference();
    preference.addEventListener('change', updatePreference);
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      preference.removeEventListener('change', updatePreference);
      observer.disconnect();
    };
  }, []);

  const stopped = paused || reducedMotion;
  useEffect(() => {
    if (!visible || stopped) return;
    const timer = window.setTimeout(() => setActive(current => (current + 1) % 3), 6000);
    return () => window.clearTimeout(timer);
  }, [active, visible, stopped]);

  const icons = [<MailOutlined />, <MessageOutlined />, <SendOutlined />];
  const scene = c.scenes[active];
  return <section className='collab-studio' ref={sectionRef} data-running={visible && !stopped}>
    <div className='home-wrap studio-layout'>
      <div className='studio-story'>
        <span className='section-kicker'>{c.eyebrow}</span>
        <h2>{c.title[0]}<br /><em>{c.title[1]}</em></h2>
        <p>{c.description}</p>
        <div className='studio-social-proof'><div className='studio-social-avatars' aria-hidden='true'>{[0, 1, 2].map(i => <Portrait variant={i} key={i} />)}</div><div><strong>{c.users}</strong><span>{c.trust}</span></div></div>
        <div className='studio-facts'>{c.stats.map(([value, label]) => <div key={label}><strong>{value}</strong><span>{label}</span></div>)}</div>
      </div>
      <div className='studio-experience'>
        <div className='studio-channel-nav' role='group' aria-label={c.workspace}>{c.channels.map((label, index) => <button type='button' key={label} className={index === active ? 'is-active' : ''} aria-pressed={index === active} onClick={() => {setActive(index); setPaused(true);}}>
          {icons[index]}<span>{label}</span><span className='studio-tab-progress' key={`${index}-${active}-${stopped}`} />
        </button>)}</div>
        <div className='studio-stage'>
          <div className='studio-backplate' aria-hidden='true' />
          <div className='studio-window'>
            <div className='studio-window-top'><span className='studio-brand-mark'>w.</span><b>{c.workspace}</b><span className='studio-demo-label'>{c.demo}</span></div>
            <div className={'studio-scene studio-scene-' + active} key={active}>
              <span className='studio-scene-number'>0{active + 1} <span>/ 03</span></span>
              <h3>{scene.label}</h3><p className='studio-scene-caption'>{scene.caption}</p>
              {active === 0 && <div className='studio-email-preview'><div className='studio-email-heading'><span><MailOutlined /></span><div><small>{scene.status}</small><h4>{scene.title}</h4></div></div><p>{scene.body}</p><div className='studio-email-recipient'><div className='studio-mini-avatar'><Portrait variant={0} /></div><span>Alex</span><span className='studio-paper-plane'><SendOutlined /></span></div></div>}
              {active === 1 && <div className='studio-chat-preview'><div className='studio-chat-message'><div className='studio-mini-avatar'><Portrait variant={0} /></div><div><small>{scene.title}</small><p>{scene.body}</p></div></div><div className='studio-chat-answer'><p>{scene.status}</p><span className='studio-chat-check'><CheckOutlined /></span></div><div className='studio-typing' aria-hidden='true'><i /><i /><i /></div></div>}
              {active === 2 && <div className='studio-invite-preview'><div className='studio-invite-icon'><ShopOutlined /></div><span>{scene.status}</span><h4>{scene.title}</h4><p>{scene.body}</p><div className='studio-invite-people'>{[0, 3, 4].map(i => <Portrait variant={i} key={i} />)}<span><ArrowRightOutlined /></span><SendOutlined /></div></div>}
              <div className='studio-scene-tag'><span />{scene.tag}</div>
            </div>
          </div>
          <div className='studio-creator-strip'><div className='studio-creator-portraits' aria-hidden='true'>{[0, 1, 2, 3, 4, 5].map(i => <div key={i} style={{animationDelay:`${i * -0.65}s`}}><Portrait variant={i} /></div>)}</div><span>{c.creators}</span></div>
        </div>
        <div className='studio-playback'><span>WOWBIT CREATOR · OUTREACH STUDIO</span><button type='button' aria-pressed={!stopped} onClick={() => {setPaused(!stopped); setReducedMotion(false);}}>{stopped ? <PlayCircleOutlined /> : <PauseOutlined />}{stopped ? c.play : c.pause}</button></div>
      </div>
    </div>
  </section>;
}
